import addDays from 'date-fns/addDays';
import { formatDate } from 'helpers/date';
import * as Rx from 'rxjs';
import { debounceTime, finalize, map, switchMap, tap } from 'rxjs/operators';
import type {
  FinancialStatementEntry,
  FinancialStatementEntryDetails,
  FinancialStatementFilter
} from 'v2/interfaces/financial';
import type { IPaginationParams, IPaginationResponse } from 'v2/interfaces/pagination';
import apiService from 'v2/services/api';
import type { ApiService } from 'v2/services/api';

import { financialStatementService, InitialPagination } from '../statement';
import type { ListOrder } from '../statement';

class FinancialStatementBlocked {
  private loading$ = new Rx.BehaviorSubject<boolean>(true);
  private totalBalance$ = new Rx.BehaviorSubject<number>(null);

  private loadingDetails$ = new Rx.BehaviorSubject<boolean>(true);
  private financialStatementBlocked$ = new Rx.BehaviorSubject<FinancialStatementEntry[]>(null);
  private paginationParams$ = new Rx.BehaviorSubject<IPaginationParams>(InitialPagination);
  private paginationResponse$ = new Rx.BehaviorSubject<IPaginationResponse>(null);

  private financialStatementBlockedDetails$ = new Rx.BehaviorSubject<FinancialStatementEntryDetails[]>(null);
  private paginationParamsDetails$ = new Rx.BehaviorSubject<IPaginationParams>(InitialPagination);
  private paginationResponseDetails$ = new Rx.BehaviorSubject<IPaginationResponse>(null);

  constructor(private apiService: ApiService) {}
  public getTotalBalance = (): Rx.Observable<number> => this.totalBalance$;

  public getFinancialStatementBlocked = (): Rx.Observable<FinancialStatementEntry[]> => this.financialStatementBlocked$;
  public getPaginationParams = (): Rx.Observable<IPaginationParams> => this.paginationParams$;
  public getPaginationResponse = (): Rx.Observable<IPaginationResponse> => this.paginationResponse$;

  public getFinancialStatementBlockedDetails = (): Rx.Observable<FinancialStatementEntryDetails[]> =>
    this.financialStatementBlockedDetails$;
  public getPaginationParamsDetails = (): Rx.Observable<IPaginationParams> => this.paginationParamsDetails$;
  public getPaginationResponseDetails = (): Rx.Observable<IPaginationResponse> => this.paginationResponseDetails$;

  public isLoading = () => this.loading$;
  public isLoadingDetails = () => this.loadingDetails$;

  public setPagination(pagination: IPaginationParams): void {
    this.paginationParams$.next(pagination);
  }

  public resetPage = () => {
    this.paginationParams$.next({ ...this.paginationParams$.value, current_page: 1 });
    return this.getPaginationParams();
  };

  public getNextPageDetails(pagination: IPaginationParams): void {
    this.paginationParamsDetails$.next(pagination);
  }

  public resetPageDetails = () => {
    this.financialStatementBlockedDetails$.next(undefined);
    this.paginationParamsDetails$.next({ ...this.paginationParamsDetails$.value, current_page: 1 });
    return this.getPaginationParamsDetails();
  };

  public formatFilter = (filter: FinancialStatementFilter) => {
    const newFilter: Partial<FinancialStatementFilter> = {};
    const daysToAdd = parseInt(filter.periodPreset) - 1;
    const startDateObj = addDays(new Date(), 1);
    const startDate = formatDate(startDateObj, 'yyyy-MM-dd');
    const endDate = formatDate(addDays(startDateObj, daysToAdd), 'yyyy-MM-dd');

    newFilter.transacted_at_initial = startDate;
    newFilter.transacted_at_final = endDate;

    if (filter.search_text) {
      newFilter.search_type = filter.search_type;
      newFilter.search_text = filter.search_text;
    }

    return newFilter;
  };

  public loadData(order?: ListOrder) {
    return financialStatementService.getFilters().pipe(
      map(filter => this.formatFilter(filter)),
      switchMap(filter =>
        this.resetPage().pipe(
          map(params => ({
            ...filter,
            ...{
              current_page: params.current_page < 0 ? 1 : params.current_page,
              per_page: params.per_page,
              order: order || 'DESC'
            }
          }))
        )
      ),
      debounceTime(10),
      switchMap(filter => {
        this.loading$.next(true);
        return this.apiService
          .get('financial-statement/blocked', filter)
          .pipe(finalize(() => this.loading$.next(false)));
      }),
      tap(response => this.financialStatementBlocked$.next(response.data)),
      tap(response => this.totalBalance$.next(response.total_balance)),
      tap(response => this.paginationResponse$.next(response.pagination))
    );
  }

  public loadDetails(transactionDate: string) {
    return financialStatementService.getFilters().pipe(
      map(filter => this.formatFilter(filter)),
      switchMap(filter =>
        this.resetPageDetails().pipe(
          map(params => ({
            ...filter,
            blocked_balance: true,
            transaction_date: transactionDate,
            current_page: params.current_page,
            per_page: params.per_page
          }))
        )
      ),
      debounceTime(10),
      switchMap(filter => {
        this.loadingDetails$.next(true);
        return this.apiService
          .get('financial-statement/detailed', filter)
          .pipe(finalize(() => this.loadingDetails$.next(false)));
      }),
      tap(response => this.financialStatementBlockedDetails$.next(response.data)),
      tap(response => this.paginationResponseDetails$.next(response.pagination))
    );
  }
}

export const financialStatementBlockedService = new FinancialStatementBlocked(apiService);
